import { Avatar } from '@seek/avatar';
import { useTranslations } from '@vocab/react';
import { Box, Button, Heading, Inline, Stack, Text } from 'braid-design-system';
import { useCallback, useRef, useState } from 'react';

import type { ProfileBase } from '../../../types/profile.types';
import { getAvatarDisplayProps } from '../../../utils/getAvatarDisplayProps';
import { LastInteraction } from '../../Profile/LastInteraction/LastInteraction';
import { Location } from '../../Profile/Location/Location';
import { MaybeApproachableBadge } from '../../Profile/MaybeApproachableBadge/MaybeApproachableBadge';
import { Nationality } from '../../Profile/Nationality/Nationality';
import { Salary } from '../../Profile/Salary/Salary';
import { VerifiedCredentialBadge } from '../../Profile/VerifiedCredentialBadge/VerifiedCredentialBadge';
import { WorkHistory } from '../../Profile/WorkHistory/WorkHistory';
import { ReportProfileForm } from '../../ReportProfileForm/ReportProfileForm';
import { VerifiedIdTickTooltip } from '../../VerifiedIdTickTooltip/VerifiedIdTickTooltip';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { LastUpdated } from '../LastUpdated/LastUpdated';

import translations from './.vocab';

interface ProfileViewCardProps {
  profile: ProfileBase;
  isSignedIn: boolean;
  profileUrl: string; // This is required (even if sometimes passed as empty string) to ensure that it isn't forgotten when used elsewhere.

  submitProfileReport?: (
    reason: string,
    comment: string,
    profileSlug: string,
  ) => Promise<boolean>;
  renderActions?: (profile: ProfileBase) => React.ReactNode;
  onLastInteractionClick?: () => void;
}

export const ProfileViewCard = ({
  profile,
  isSignedIn,
  profileUrl,
  submitProfileReport,
  renderActions,
  onLastInteractionClick,
}: ProfileViewCardProps) => {
  const {
    approachability,
    displayName,
    digitalIdentity,
    hasVerifiedCredentials,
    workHistories,
    homeLocation,
    salary,
    nationalities,
    hasEthicalHiring,
    lastInteractionDuration,
    lastModifiedDurationLabel,
    profileAvatarUrls,
  } = profile;

  const { t } = useTranslations(translations);

  const hasWorkHistories = workHistories && workHistories.length > 0;
  const [isCopied, setIsCopied] = useState(false);
  const [isReportFormOpen, setIsReportFormOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Check if render actions is undefined because there are two separate apps (HBS and CBS)
  // HBS uses render actions and CBS does not. Hence, we don't want the menu to be displayed if its HBS
  const shouldDisplayMenu = isSignedIn && renderActions === undefined;

  const handleCopyLink = useCallback(() => {
    const fullUrl = `${window.location.origin}${profileUrl}`;

    navigator.clipboard.writeText(fullUrl);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsCopied(true);

    // Reset after 2.8 seconds
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, 2800);
  }, [profileUrl]);

  const { photoUrl, variant } = getAvatarDisplayProps({
    hasEthicalHiring,
    imageUrl: profileAvatarUrls?.image256Url ?? undefined,
  });

  return (
    <Stack space="small">
      <Box marginBottom="xxsmall" position="relative">
        <Box position="absolute" style={{ top: '-88px' }}>
          <Avatar
            border
            name={displayName}
            photoUrl={photoUrl}
            size="xlarge"
            variant={variant}
          />
        </Box>
      </Box>
      <Inline space="medium" alignY="top">
        <Box style={{ marginRight: 'auto' }}>
          <Stack space="small">
            <Inline space="xxsmall" alignY="center">
              <Heading level="2">{displayName}</Heading>
              {digitalIdentity.isVerified ? (
                <Text>
                  {' '}
                  <VerifiedIdTickTooltip
                    hasEthicalHiring={Boolean(hasEthicalHiring)}
                    isSignedIn={isSignedIn}
                    verifiedName={digitalIdentity.verifiedName}
                    profileName={displayName}
                  />
                </Text>
              ) : null}
            </Inline>
            <Inline space="xxsmall" alignY="center">
              {approachability === 'high' ? (
                <Box aria-label="May be approachable">
                  <MaybeApproachableBadge />
                </Box>
              ) : null}
              {hasVerifiedCredentials || digitalIdentity.isVerified ? (
                <Box aria-label="Verified ID">
                  <VerifiedCredentialBadge />
                </Box>
              ) : null}
            </Inline>
            <Box marginTop="xxsmall">
              <Stack space="small">
                {hasWorkHistories && (
                  <WorkHistory
                    workHistories={workHistories}
                    count={1}
                    hideDuration={true}
                    boldText={false}
                    showIcon
                  />
                )}

                <Location homeLocation={homeLocation} />
                {nationalities ? (
                  <Nationality nationalities={nationalities} />
                ) : null}
                {salary ? <Salary salary={salary} /> : null}
                {lastInteractionDuration ? (
                  <Box paddingTop="xsmall">
                    <LastInteraction
                      lastInteractionDuration={lastInteractionDuration}
                      onClick={() => onLastInteractionClick?.()}
                    />
                  </Box>
                ) : null}
                {renderActions ? (
                  <Box paddingTop="xsmall">{renderActions(profile)}</Box>
                ) : null}

                {profileUrl || shouldDisplayMenu ? (
                  <Box marginTop="small">
                    <Inline space="large">
                      {profileUrl ? (
                        <Button
                          size="small"
                          tone="neutral"
                          variant="ghost"
                          onClick={handleCopyLink}
                        >
                          {isCopied ? t('LINK_COPIED') : t('SHARE')}
                        </Button>
                      ) : null}

                      {shouldDisplayMenu && (
                        <DropdownMenu
                          setIsReportFormOpen={setIsReportFormOpen}
                        />
                      )}
                    </Inline>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {lastModifiedDurationLabel ? (
          <LastUpdated duration={lastModifiedDurationLabel} />
        ) : null}
      </Inline>

      <ReportProfileForm
        open={isReportFormOpen}
        onClose={() => setIsReportFormOpen(false)}
        onSubmit={async (reason, comments) => {
          if (submitProfileReport === undefined) {
            return new Promise((resolve) => resolve(false));
          }
          return await submitProfileReport(reason, comments, profile.slug);
        }}
      />
    </Stack>
  );
};
